import $ from 'jquery';
import {Model} from 'backbone';
import {getFetch} from '../../sinclair/sinclair.ajax';
import lscache from 'lscache';
import DebugModel from '../../models/common/debugModel';
import TargetCollection from '../../collections/common/targetCollection';
import {getSiteUuid} from '../../models/stories/syndicationTargetModel';
import permissionSets from './permissionSetsMap';
import {chain, contains, filter, pipe, prop, path, sortBy, uniqBy, uniq} from 'ramda';
import {isEqual, isObject} from 'lodash';
import {tagCategories as availableCategories} from '../../models/common/tagModel';

const getPackageTypesFromTargets = targets => uniq(chain(path(['site', 'packageTypes']), targets));

const siteCallLetters = path(['site', 'callLetters']);

const cleanStations = pipe(
    uniqBy(siteCallLetters),
    sortBy(siteCallLetters),
);

/**
 * The Uuid used for properties in the nmp/v1 system
 *  (sometimes referred to as site)
 *
 * @typedef {string} PropertyUuid
 */

export default Model.extend({
    urlRoot: '/platform/facade/user/active/compressed',
    idAttribute: 'uuid',
    cache: lscache,
    debugModel: null,
    defaults: {
        uuid: null,
        // TODO: switch this over to the userModel object
        user: {
            uuid: null,
            activeDirectoryId: null,
            created: null,
            homeStation: null,
            imageThbUrl: null,
            imageUrl: null,
            status: null,
            firstName: null,
            lastName: null,
            username: null,
            email: null,
        },
        availableTargets: [],
        availableCategories: [],
        permissions: null,
        roles: null,
    },
    _availableTargetsCache: {
        cachedIds: [],
        targetJSON: [],
    },
    initialize(opts = {}) {
        this.on('change', () => (this.siteUuidPermissions = this.getTargetsWithPublishPermissions().map(getSiteUuid)));

        if (isObject(window.frontendStoryLineConfig) && window.frontendStoryLineConfig.buildNumber) {
            this.debugModel = new DebugModel(window.frontendStoryLineConfig);
        } else {
            this.debugModel = new DebugModel();
        }
        // This is done after we initialize the debug model since it is currently behind a feature flag.

        this.set('availableTargets', new TargetCollection());

        if (opts.test) {
            this.fetch = () => $.Deferred().resolve();
        } else {
            this.reloadInterval = setInterval(() => {
                //jdj-2015-03-28 - todo - handle fj error conditions, e.g. 419 conflict conditions
                this.reload();
            }, 300000);
        }
        const cache = this.isCached();
        if (cache) {
            const targets = this.get('availableTargets');
            targets.reset(cache.availableTargets);
            cache.availableTargets = targets;
            cache.allowedPackageTypes = getPackageTypesFromTargets(targets.toJSON());
            this.set(cache);
        }
    },
    isCached() {
        return lscache.get('userModel');
    },
    parse(data = {}) {
        const extracted = data.hasOwnProperty('data') ? data.data : data;
        let model = extracted && (Array.isArray(extracted) && extracted.length > 0) ? extracted[0] : extracted;
        model = {...model, availableCategories};

        const newTargets = model.availableTargets || [];
        model = {...model, allowedPackageTypes: getPackageTypesFromTargets(newTargets)};

        const availableTargets = this.get('availableTargets');
        if (availableTargets instanceof TargetCollection) {
            availableTargets.reset(newTargets);
            model = {...model, availableTargets: new TargetCollection(availableTargets.toJSON())};
        } else {
            model = {...model, availableTargets: new TargetCollection(newTargets)};
        }

        const filteredAvailableTargets = availableTargets.map(i => {
            const {site: {attributes: site}} = i.attributes;
            return {
                callLetters: site.callLetters,
                city: site.city,
                imageUrl: site.imageUrl,
                propertyUuid: site.propertyUuid,
                siteSlug: site.siteSlug,
                state: site.state,
            };
        });

        lscache.set('userModel', model, 30);
        lscache.set('chimUserProperties', filteredAvailableTargets);
        lscache.set('chimUserInfo', {
            email: model.user.email,
            firstName: model.user.firstName,
            lastName: model.user.lastName,
        });

        return model;
    },
    getFullName() {
        const {firstName, lastName} = this.get('user');
        return `${firstName} ${lastName}`;
    },
    getStations() {
        return cleanStations(this.getAvailableTargetsJSON());
    },
    getAvailableTargetsJSON() {
        const availableTargets = this.get('availableTargets') || [];
        const availableTargetIds = availableTargets.map(prop('cid'));

        if (isEqual(availableTargetIds, this._availableTargetsCache.cachedIds)) {
            return this._availableTargetsCache.targetJSON;
        } else {
            this._availableTargetsCache.cachedIds = availableTargetIds;
            return (this._availableTargetsCache.targetJSON = availableTargets.toJSON());
        }
    },
    getTargetsWithPublishPermissions() {
        let allTargets = this.getAvailableTargetsJSON();
        const permissions = this.get('permissions') || {};
        const publishPermissions = permissions['com.sinclair.digital.facade.story.publish-story'];

        if (!publishPermissions || !publishPermissions.length) {
            return (allTargets = []);
        } else {
            if (publishPermissions[0] === '') {
                return allTargets;
            } else {
                return filter(target => contains(target.site.uuid, publishPermissions), allTargets);
            }
        }
    },
    canPublishTo(targetSiteUuid) {
        return this.siteUuidPermissions.includes(targetSiteUuid);
    },
    reload() {
        const opts = {
            url: this.urlRoot,
            msg: 'user information',
            before() {},
            complete() {},
        };

        this.fetch(getFetch(opts));
    },
    /**
     * Checks if the user has the permission defined in the list of permissions.
     *
     * @param {string} permission
     * @returns {boolean}
     */
    userHasAnyPermission(permission) {
        const permissions = this.get('permissions');

        return permissions !== null && !!permissions[permission];
    },
    //Similar to models/admin/userModel#getPropertyByUuid
    hasRoleFor(roleToFind, uuid) {
        const dottedRoleToFind = '.' + roleToFind;
        const roles = this.get('roles') || {};
        for (const role in roles) {
            if (!roles.hasOwnProperty(role)) {
                continue;
            }
            if (!role.includes(dottedRoleToFind, role.length - dottedRoleToFind.length)) {
                continue;
            }
            if (roles[role].global || !uuid) {
                return true;
            }
            if (roles[role].propertyUuid && roles[role].propertyUuid === uuid) {
                return true;
            }
            if (roles[role].propertyUuids && roles[role].propertyUuids.includes && roles[role].propertyUuids.includes(uuid)) {
                return true;
            }
        }

        return false;
    },
    isAdmin() {
        return this.hasRoleFor('admin', 'global');
    },
    /**
     * Takes a permission string and an optional context identifier
     *  When optional context identifier is 'global', function will check if user has the permission for any property.
     *  When optional context identifier is '', function will check if user has permission assigned from "global" role
     *  When optional context identifier is propertyUuid, then returns true if user has permission for that property.
     *  Otherwise, returns false
     *
     * @signature ._userHasPermission :: (String, String?) -> Boolean
     * @param {string} permission
     * @param {'global'|''|PropertyUuid} [uuid]
     * @returns {boolean}
     * @private
     */
    _userHasPermission(permission, uuid) {
        const permissions = this.get('permissions');

        if (!permissions) {
            return false;

            // CONTEXTUAL CHECK - checking if you can do something for a specific property
        } else if (permissions[permission] && uuid !== 'global') {
            return permissions[permission].includes('') || permissions[permission].includes(uuid);
        }
        // GLOBAL CHECK - checking to see if you have that permission at all
        return !!permissions[permission];
    },

    /**
     * Takes a permissionSet name (see: permissionSetsMap.js for all permission sets) and an
     * optional context identifier.  See notes for _userHasPermission for additional details
     * about behavior.
     *
     * @signature .userHasPermissionSet :: (String, String?) -> Boolean
     * @param {string} permissionSet
     * @param {'global'|''|PropertyUuid} [uuid=null]
     * @returns {boolean}
     */
    userHasPermissionSet(permissionSet, uuid = null) {
        if (permissionSets.has(permissionSet)) {
            return permissionSets.get(permissionSet).every(permission => this._userHasPermission(permission, uuid));
        }

        console.error(permissionSet + ' is an not a valid permission');
        return false;
    },
    /**
     * This should have the same logical effect as calling userHasPermissionSet(permissionSet, 'global');
     *
     * @param {string} permissionSet
     * @returns {boolean}
     */
    userHasAnyPermissionSet(permissionSet) {
        if (permissionSets.has(permissionSet)) {
            return permissionSets.get(permissionSet).every(permission => this.userHasAnyPermission(permission));
        }

        console.error(permissionSet + ' is an not a valid permission');
        return false;
    },
    /**
     * This function call is named in a misleading way, would be best to generally not use.
     * This function takes a permissionSet name and checks to see if the user has any access to
     *  the permission, not has been granted the permission from a global role, like one may
     *  expect.
     *
     * @param {string} permissionSet
     * @returns {boolean}
     */
    userHasGlobalPermissionSet(permissionSet) {
        return this.userHasPermissionSet(permissionSet, 'global');
    },

    //jdj-2015-03-27 - adding sub-permission search, for nav rollup features
    userHasSubPermission(searchPermission) {
        let searchFound = false;

        $.each(this.get('permissions'), function(key) {
            if (key.includes(searchPermission)) {
                searchFound = true;

                return false;
            } else {
                return true;
            }
        });

        return searchFound;
    },

    isPackageTypeAllowed(packageType) {
        const packageTypes = this.get('allowedPackageTypes');
        return packageTypes && packageTypes.length && packageTypes.includes(packageType);
    },

    // isAnyPackageTypeAllowed :: [String] -> userModel -> Boolean
    isAnyPackageTypeAllowed(packageTypes) {
        const allowedPackageTypes = this.get('allowedPackageTypes');
        return packageTypes.some(pt => allowedPackageTypes.includes(pt));
    },
});
